import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Phone, MessageCircle, Gift, CheckCircle2, Loader2, XCircle } from "lucide-react";
import API from "../api/axiosConfig";
import CountryPicker from "../components/CountryPicker";
import { getCleanPhone } from "../utils/phoneCountryCodes";

export default function AddWhatsapp() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [info, setInfo]       = useState(null);
  const [loadError, setLoadError] = useState("");

  const [phone, setPhone]                       = useState("");
  const [phoneCountryCode, setPhoneCountryCode]  = useState("+57");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [pointsAwarded, setPointsAwarded] = useState(null);

  useEffect(() => {
    API.get(`/whatsapp-optin/${token}`)
      .then((res) => setInfo(res.data?.data))
      .catch((err) => setLoadError(err.response?.data?.message || "Este enlace no es válido o ya expiró."))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const { clean, expected } = getCleanPhone(phone, phoneCountryCode);
    if (clean.length !== expected) {
      setSubmitError(`Ingresá un número válido (${expected} dígitos).`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await API.post(`/whatsapp-optin/${token}`, { phone: clean, phoneCountryCode });
      setPointsAwarded(res.data?.data?.pointsAwarded ?? 0);
    } catch (err) {
      setSubmitError(err.response?.data?.message || "No se pudo guardar tu WhatsApp. Intentá de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
        <Loader2 size={28} className="animate-spin text-supernova-500" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <XCircle size={32} className="text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Enlace inválido</h1>
          <p className="text-sm text-gray-500">{loadError}</p>
        </div>
      </div>
    );
  }

  if (pointsAwarded !== null || info?.alreadyAdded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={36} className="text-green-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">¡Listo, {info?.name?.split(" ")[0]}!</h1>
          {pointsAwarded > 0 ? (
            <p className="text-sm text-gray-500">
              Agregamos tu WhatsApp y te regalamos <strong className="text-gray-700">{pointsAwarded} QPoints</strong> 🎉
            </p>
          ) : (
            <p className="text-sm text-gray-500">Ya tenías tu WhatsApp registrado. ¡Gracias!</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">

        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-5">
          <MessageCircle size={22} className="text-green-600" />
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-1">Hola, {info?.name?.split(" ")[0]}</h1>
        <p className="text-sm text-gray-500 mb-6">
          Agregá tu número de WhatsApp para enterarte del estado de tus pedidos.
          {info?.points > 0 && (
            <span className="flex items-center gap-1.5 mt-2 text-supernova-600 font-semibold">
              <Gift size={14} /> Ganás {info.points} QPoints de regalo
            </span>
          )}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Número de WhatsApp</label>
            <div className="flex gap-2">
              <CountryPicker value={phoneCountryCode} onChange={(c) => { setPhoneCountryCode(c); setPhone(""); }} />
              <div className="relative flex-1">
                <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="tel"
                  placeholder="Número de celular"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full pl-9 pr-3 py-3 border border-gray-300 rounded-xl outline-none transition focus:ring-2 focus:ring-green-400"
                  required
                />
              </div>
            </div>
          </div>

          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-xl bg-supernova-400 hover:bg-supernova-500 text-black font-bold transition flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? (
              <><Loader2 size={16} className="animate-spin" /> Guardando...</>
            ) : (
              "Agregar mi WhatsApp"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
